import React from 'react';
import PropTypes from 'prop-types';
import Card from '../../../../components/layout/Card/Card';
import FormRow from '../../../../components/form/FormRow/FormRow';
import FormColumn from '../../../../components/form/FormColumn/FormColumn';
import ResultIndicator from './ResultIndicator';
import { formatCurrency, formatDate, formatPercentage } from '../../../../utils/formatters';
import { getTipoParcela } from '../../../../utils/constantes';
import styles from './FlexSimulationResult.module.css';

const FlexSimulationResult = ({ result, pagadoria }) => {
  if (!result) return null;

  // Calcular totais
  const totalSinais = result.parcelas
    .filter(p => p.tipo === 3)
    .reduce((acc, p) => acc + (p.valorNominal || 0), 0);

  const totalIntermediarias = result.parcelas
    .filter(p => p.tipo === 1)
    .reduce((acc, p) => acc + (p.valorNominal || 0), 0);

  // Carta de Crédito: parcela tipo 7 substitui o ato quando aplicada; se o valor da carta
  // não cobrir o ato integralmente, uma parcela de Sinal complementar (atoComplementar) é criada.
  const parcelaCartaDeCredito = result.parcelas.find(p => p.tipo === 7);
  const parcelaAtoComplementar = result.parcelas.find(p => p.atoComplementar);

  // Calcular porcentagem de financiamento (para FB)
  const isFinanciamentoBancario = result.valorFinanciado !== undefined && result.valorFinanciado > 0;
  const valorFinal = result.valorFinal || 0;
  const valorFinanciado = result.valorFinanciado || 0;
  const percentualFinanciado = valorFinal > 0 
    ? ((valorFinanciado / valorFinal) * 100).toFixed(1)
    : 0;

  return (
    <div className={styles.simulationResult}>
      <h3 className={styles.simulationTitle}>Resultado da Simulação</h3>

      {/* Resumo da Proposta */}
      <Card title="Resumo da Proposta">
        <FormRow>
          <FormColumn cols={4}>
            <ResultIndicator
              label="Valor de Tabela"
              value={formatCurrency(result.valorTabela)}
            />
          </FormColumn>
          <FormColumn cols={4}>
            <ResultIndicator
              label="Aumento"
              value={formatCurrency(result.aumento)}
            />
          </FormColumn>
          <FormColumn cols={4}>
            <ResultIndicator
              label="Desconto"
              value={formatCurrency(result.desconto)}
            />
          </FormColumn>
   
          <FormColumn cols={4}>
            <ResultIndicator
              label="Prazo de Obra"
              value={result.prazoObra}
            />
          </FormColumn>
          <FormColumn cols={4}>
            <ResultIndicator
              label="AMP"
              value={formatPercentage(result.amp * 0.01)}
            />
          </FormColumn>
          <FormColumn cols={4}>
            <ResultIndicator
              label="Valor Final"
              value={formatCurrency(result.valorFinal)}
            />
          </FormColumn>

          <FormColumn cols={4}>
            <ResultIndicator
              label="Valor Final c/ FGTS"
              value={formatCurrency(result.valorFinalComFgts)}
            />
          </FormColumn>
          {pagadoria && (
            <FormColumn cols={4}>
              <ResultIndicator
                label="Valor Comissão (via pagadoria)"
                value={formatCurrency(result.valorComissao)}
              />
            </FormColumn>
          )}
          <FormColumn cols={4}>
            <ResultIndicator
              label="Margem"
              value={formatPercentage(result.margem* 0.01)}
            />
          </FormColumn>
     
          <FormColumn cols={4}>
            <ResultIndicator
              label="Total Sinais"
              value={formatCurrency(totalSinais)}
            />
          </FormColumn>
          <FormColumn cols={4}>
            <ResultIndicator
              label="Total Intermediárias"
              value={formatCurrency(totalIntermediarias)}
            />
          </FormColumn>
          {parcelaCartaDeCredito && (
            <FormColumn cols={4}>
              <ResultIndicator
                label="Valor Carta de Crédito"
                value={formatCurrency(parcelaCartaDeCredito.valorNominal)}
              />
            </FormColumn>
          )}
          {parcelaAtoComplementar && (
            <FormColumn cols={4}>
              <ResultIndicator
                label="Ato Complementar"
                value={formatCurrency(parcelaAtoComplementar.valorNominal)}
              />
            </FormColumn>
          )}
          <FormColumn cols={4}>
            <ResultIndicator
              label="FGTS"
              value={formatCurrency(result.fgts)}
            />
          </FormColumn>
          {isFinanciamentoBancario && (
            <FormColumn cols={4}>
              <ResultIndicator
                label={`Valor Financiado (${percentualFinanciado}%)`}
                value={formatCurrency(valorFinanciado)}
              />
            </FormColumn>
          )}
        </FormRow>
      </Card>

      {/* Grupos de Parcelas */}
      {result.informativoGrupos && result.informativoGrupos.length > 0 && (
        <Card title="Grupos de Parcelas">
          <FormRow>
            <FormColumn cols={12}>
              <div className={styles.tableResponsive}>
                <table className={styles.table}>
                  <thead>
                    <tr>
                      <th>Grupo</th>
                      <th>Valor Nominal da Parcela (R$)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.informativoGrupos.map((grupo, index) => (
                      <tr key={`grupo-${index}`}>
                        <td>{grupo.nomeGrupo}</td>
                        <td>{formatCurrency(grupo.valorNominalParcela)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </FormColumn>
          </FormRow>
        </Card>
      )}

      {/* Cronograma de Pagamentos */}
      <Card title="Cronograma de Pagamentos">
        <FormRow>
          <FormColumn cols={12}>
            <div className={styles.tableResponsive}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th>Vencimento</th>
                    <th>Tipo</th>
                    <th>Valor Nominal (R$)</th>
                    <th>Fator Variação</th>
                    <th>Projeção (R$)</th>
                  </tr>
                </thead>
                <tbody>
                  {result.parcelas.map((parcela, index) => {
                    const uniqueKey = `parcela-${parcela.dataVencimento}-${parcela.tipo}-${index}`;
                    return (
                      <tr key={uniqueKey}>
                        <td>{formatDate(parcela.dataVencimento)}</td>
                        <td>{getTipoParcela(parcela, index, result.parcelas)}</td>
                        <td>{formatCurrency(parcela.valorNominal)}</td>
                        <td>{result.projecao[index]?.toFixed(4)}</td>
                        <td>{formatCurrency(parcela.valorReal)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </FormColumn>
        </FormRow>
      </Card>
    </div>
  );
};

FlexSimulationResult.propTypes = {
  result: PropTypes.shape({
    idProposta: PropTypes.string,
    idUnidade: PropTypes.string,
    linhaProduto: PropTypes.string,
    tipoCanal: PropTypes.string,
    equipe: PropTypes.string,
    prazoObra: PropTypes.number,
    sad: PropTypes.bool,
    pagadoria: PropTypes.bool,
    tipoCorrecao: PropTypes.string,
    projecao: PropTypes.any,
    tipoFluxo: PropTypes.string,
    valorTabela: PropTypes.number,
    aumento: PropTypes.number,
    desconto: PropTypes.number,
    amp: PropTypes.number,
    bomPagadorPagadoria: PropTypes.number,
    valorFinal: PropTypes.number,
    fgts: PropTypes.number,
    valorFinalComFgts: PropTypes.number,
    valorFinanciado: PropTypes.number,
    valorComissao: PropTypes.number,
    margem: PropTypes.number,
    numeroParcela: PropTypes.number,
    psaPadrao: PropTypes.number,
    psbPadrao: PropTypes.number,
    valorPSBTotal: PropTypes.number,
    parcelas: PropTypes.arrayOf(
      PropTypes.shape({
        id: PropTypes.string,
        idProposta: PropTypes.string,
        tipo: PropTypes.number,
        valorNominal: PropTypes.number,
        valorReal: PropTypes.number,
        dataVencimento: PropTypes.string,
        tipoSinal: PropTypes.number,
        atoComplementar: PropTypes.bool
      })
    ),
    informativoGrupos: PropTypes.arrayOf(
      PropTypes.shape({
        nomeGrupo: PropTypes.string,
        valorNominalParcela: PropTypes.number
      })
    )
  }),
  pagadoria: PropTypes.bool,
  prazoObra: PropTypes.shape({
    prazoMeses: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    ampPercentual: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
  })
};

FlexSimulationResult.defaultProps = {
  pagadoria: false
};

export default FlexSimulationResult;
